import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Target } from "lucide-react";
import { BRAND, IMAGES, buildWhatsappLink } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import AnimatedCounter from "@/components/AnimatedCounter";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" },
  }),
};

export default function Hero() {
  return (
    <section id="inicio" className="relative scroll-mt-nav overflow-hidden bg-efory-ink text-white">
      {/* Background image + overlays */}
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-efory-ink via-efory-ink/85 to-efory-ink" />
        <div className="absolute inset-0 bg-grid-dark opacity-60" />
      </div>
      <div className="absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full bg-efory-blue/20 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-32 pb-24 md:pt-40 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left text */}
        <div className="lg:col-span-7">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-efory-blue mb-7"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-efory-blue animate-pulse" />
            Soluções financeiras e comerciais
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight"
          >
            Sua <span className="text-gradient-blue">operação</span> está pronta para crescer com{" "}
            <span className="text-gradient-blue">previsibilidade</span>?
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-6 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed"
          >
            A EFORY conecta <span className="text-efory-blue font-semibold">vendas e gestão</span>,
            integrando marketing, CRM,{" "}
            <span className="text-efory-blue font-semibold">processos</span> comerciais e gestão{" "}
            <span className="text-efory-blue font-semibold">financeira</span> para acelerar o{" "}
            <span className="text-efory-blue font-semibold">crescimento</span> do seu negócio com{" "}
            <span className="text-efory-blue font-semibold">previsibilidade</span> e resultado.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <div className="flex flex-col items-start gap-1.5">
              <a href="#agendamento">
                <Button
                  size="lg"
                  data-testid="hero-schedule-button"
                  className="group bg-efory-blue hover:bg-efory-bluedark text-white rounded-full px-7 h-13 py-6 text-base font-semibold transition-all duration-300 hover:scale-[1.03] shadow-lg shadow-efory-blue/25"
                >
                  Diagnóstico completo em 48h
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </a>
              <span className="text-xs text-white/50 pl-2">(Sem compromisso)</span>
            </div>
            <a
              href={buildWhatsappLink("Olá EFORY! Quero falar com um especialista.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-specialist-link"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white font-medium transition-colors"
            >
              Falar com especialista
              <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/60"
          >
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-efory-blue" /> Site seguro</span>
            <span className="inline-flex items-center gap-2"><Target className="h-4 w-4 text-efory-blue" /> Diagnóstico focado em ROI</span>
          </motion.div>
        </div>

        {/* Right stats card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          className="lg:col-span-5"
        >
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-7 md:p-9 overflow-hidden">
            {/* Lightning/electric backdrop glow */}
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute -top-24 -right-16 h-[320px] w-[320px] rounded-full bg-efory-blue/20 blur-[100px]" />
              <div className="absolute -bottom-24 -left-16 h-[260px] w-[260px] rounded-full bg-efory-blue/10 blur-[110px]" />
            </div>
            <div className="absolute -inset-px rounded-3xl bg-gradient-to-b from-efory-blue/30 to-transparent opacity-40 pointer-events-none" />

            <div className="relative">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={BRAND.mark} alt="EFORY" className="h-10 w-10 object-contain" />
                  <h3 className="font-heading text-lg font-extrabold tracking-[0.18em] leading-none">EFORY</h3>
                </div>
                <span className="text-xs font-medium text-efory-blue">Resultados acumulados</span>
              </div>

              {/* Section label */}
              <p className="mt-8 text-[13px] font-medium uppercase tracking-[0.18em] text-white/55">
                EFORY EM NÚMEROS
              </p>

              {/* Big headline: R$ 5,5 mi+ */}
              <div className="mt-3 font-heading text-5xl sm:text-6xl md:text-[64px] font-black leading-[0.95] text-gradient-blue">
                <AnimatedCounter prefix="R$ " to={5.5} decimals={1} suffix=" mi+" />
              </div>
              <p className="mt-3 text-base sm:text-lg text-white/80 leading-snug">
                em receita gerada para clientes
              </p>

              {/* 4 stats row */}
              <div className="mt-8 grid grid-cols-4 gap-2 md:gap-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-2 py-4 md:px-3 md:py-5">
                  <div className="font-heading text-base sm:text-lg md:text-xl lg:text-2xl font-extrabold text-efory-blue leading-none whitespace-nowrap">
                    <AnimatedCounter to={7} suffix=" anos" />
                  </div>
                  <div className="mt-2 text-[10px] md:text-[11px] text-white/55 leading-snug">
                    de experiência
                  </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-2 py-4 md:px-3 md:py-5">
                  <div className="font-heading text-base sm:text-lg md:text-xl lg:text-2xl font-extrabold text-efory-blue leading-none">
                    <AnimatedCounter prefix="+" to={250} />
                  </div>
                  <div className="mt-2 text-[10px] md:text-[11px] text-white/55 leading-snug">
                    empresas<br />atendidas
                  </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-2 py-4 md:px-3 md:py-5">
                  <div className="font-heading text-base sm:text-lg md:text-xl lg:text-2xl font-extrabold text-efory-blue leading-none whitespace-nowrap">
                    <AnimatedCounter prefix="+" to={1300} />
                  </div>
                  <div className="mt-2 text-[10px] md:text-[11px] text-white/55 leading-snug">
                    projetos e estratégias
                  </div>
                </div>
                <div className="rounded-2xl border border-efory-blue/40 bg-efory-blue/10 px-2 py-4 md:px-3 md:py-5">
                  <div className="font-heading text-base sm:text-lg md:text-xl lg:text-2xl font-extrabold text-efory-blue leading-none">
                    <AnimatedCounter to={9.1} decimals={1} />
                  </div>
                  <div className="mt-2 text-[10px] md:text-[11px] text-white/70 leading-snug">
                    índice de<br />satisfação
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
